import ReportDocumentHeader from '@/components/Reports/ReportPrivate/_comons/ReportDocumentHeader';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import TemplateReport from '@/components/_common/TemplateReport/TemplateReport';
import ReportTable, {
  ReportTableColumn,
} from '@/components/_common/TemplateReport/componentes/ReportTable';
import { useEmployeeCommissionList } from '@/core/domain/enterprise/EmployeeCommission/EmployeeCommission.hooks';
import { EmployeeCommission } from '@/core/domain/enterprise/EmployeeCommission/EmployeeCommission.types';
import { DateTimeFormat } from '@/utils/moment';
import formatterPrice from '@/utils/number/formatterPrice';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Space } from 'antd';
import moment from 'moment';
import { Suspense, useEffect } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

type SearchParams = {
  employeeIds?: string;
  from?: string;
  to?: string;
  codeWorkOrder?: string;
  licensePlate?: string;
  customerName?: string;
  groupByEmployee?: string;
};

interface ValuesSomed {
  liquidValue: number;
  baseValue: number;
  consumableUsed: number;
  commissionValue: number;
}

type CommissionsGroup = Record<number, EmployeeCommission[]>;

const ReportCommissionByEmployeePage: React.FC = () => {
  const match = useRouteMatch();
  const { search } = useLocation();
  const { groupByEmployee, ...params } = Object.fromEntries(
    new URLSearchParams(search),
  ) as SearchParams;

  const hasByGroup = JSON.parse(groupByEmployee || 'false');

  const { data, isLoading } = useEmployeeCommissionList({
    ...params,
    rows: undefined,
    from: params.from
      ? moment(params.from).startOf('day').format(DateTimeFormat)
      : undefined,
    to: params.to
      ? moment(params.to).endOf('day').format(DateTimeFormat)
      : undefined,
    employeeIds: params.employeeIds
      ?.split(',')
      .filter((id) => !!id)
      .map((id) => Number(id)),
    codeWorkOrder: params.codeWorkOrder
      ? Number(params.codeWorkOrder)
      : undefined,
    commissionReport: true,
  });
  const commissions = flatInfiniteData(data);

  useEffect(() => {
    document.title = 'comissão-funcionários';
  }, []);

  const columns: ReportTableColumn<EmployeeCommission>[] = [
    {
      width: 100,
      key: 'nameEmployee',
      title: 'Funcionário',
      ellipsis: true,
    },
    {
      width: 70,
      key: 'codeVersionWorkOrder',
      title: 'OS',
      ellipsis: true,
      render: (item) => item.codeVersionWorkOrder.replace(/\s/g, ''),
    },
    { width: 100, key: 'nameCustomer', title: 'Cliente', ellipsis: true },
    {
      width: 80,
      key: 'payDate',
      title: 'Pagamento',
      render: (item) =>
        item.payDate ? moment(item.payDate).format('DD/MM/YYYY') : '-',
      align: 'right',
    },
    {
      width: 80,
      key: 'liquidValue',
      title: 'Vl. liq.',
      render: (item) => formatterPrice(item.liquidValue),
      align: 'right',
    },
    {
      width: 80,
      key: 'baseValue',
      title: 'Vl. base',
      render: (item) => formatterPrice(item.baseValue),
      align: 'right',
    },
    {
      width: 80,
      key: 'consumableUsed',
      title: 'Insumos',
      render: (item) => formatterPrice(item.consumableUsed),
      align: 'right',
    },
    {
      width: 80,
      key: 'commissionValue',
      title: 'Comissão',
      render: (item) => formatterPrice(item.commissionValue),
      align: 'right',
    },
  ];

  function renderFooter(values: ValuesSomed, isSubTotal = false) {
    return (
      <tr>
        <td colSpan={3}></td>
        <td align="right">
          <strong>{isSubTotal ? `Subtotal` : 'Total'}</strong>
        </td>
        <td align="right">
          <strong>{formatterPrice(values.liquidValue)}</strong>
        </td>
        <td align="right">
          <strong>{formatterPrice(values.baseValue)}</strong>
        </td>
        <td align="right">
          <strong>{formatterPrice(values.consumableUsed)}</strong>
        </td>
        <td align="right">
          <strong>{formatterPrice(values.commissionValue)}</strong>
        </td>
      </tr>
    );
  }

  function calcValues(data: EmployeeCommission[]) {
    return data.reduce(
      (acc, curr) => ({
        liquidValue: acc.liquidValue + (curr.liquidValue || 0),
        baseValue: acc.baseValue + (curr.baseValue || 0),
        consumableUsed: acc.consumableUsed + (curr.consumableUsed || 0),
        commissionValue: acc.commissionValue + (curr.commissionValue || 0),
      }),
      {
        liquidValue: 0,
        baseValue: 0,
        consumableUsed: 0,
        commissionValue: 0,
      },
    );
  }

  function commissionsByEmployee() {
    return commissions.reduce((acc, curr) => {
      if (acc[curr.employeeId]) {
        return {
          ...acc,
          [curr.employeeId]: [...acc[curr.employeeId], curr],
        };
      }
      return { ...acc, [curr.employeeId]: [curr] };
    }, {} as CommissionsGroup);
  }

  useEffect(() => {
    if (match.isExact && !isLoading && !!data) {
      setTimeout(() => window.print(), 1000);
    }
  }, [match.isExact, isLoading, data]);

  return (
    <Suspense fallback={<span>Carregando...</span>}>
      {isLoading && (
        <ModalLoadingOverlay
          messages={[
            'Calculando comissão...',
            'Aguarde, gerando relatório...',
            'Quase pronto, preparando para impressão...',
          ]}
        />
      )}

      {match.isExact && !isLoading && !!commissions && (
        <TemplateReport>
          <main className="employeeCommissions">
            <Space direction="vertical" size="large" className="w-100">
              <ReportDocumentHeader title="Comissões de funcionários" />

              <div className="card">
                {!hasByGroup && (
                  <ReportTable
                    columns={columns}
                    dataSource={commissions}
                    footer={renderFooter(calcValues(commissions))}
                  />
                )}

                {hasByGroup &&
                  Object.values(commissionsByEmployee()).map(
                    (data, index, array) => (
                      <ReportTable
                        key={index}
                        columns={columns}
                        dataSource={data}
                        footer={renderFooter(calcValues(data))}
                        style={
                          index < array.length - 1 ? { marginBottom: 24 } : {}
                        }
                      />
                    ),
                  )}
              </div>
            </Space>
          </main>
        </TemplateReport>
      )}
    </Suspense>
  );
};

export default ReportCommissionByEmployeePage;
